import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';

interface ProjectHeaderProps {
  title: string;
  dateRange: string;
  description: string;
  weight: number;
  entityName?: string;
  onEdit?: () => void;
}

export default function ProjectHeader({ title, dateRange, description, weight, entityName, onEdit }: ProjectHeaderProps) {
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: colors.card,
          borderColor: colors.cardBorder,
        },
        createShadow(0, 1, 3, '#000', 0.1),
      ]}
    >
      {/* Edit button — only show when onEdit provided AND not in entity flow */}
      {onEdit && !entityName && (
        <TouchableOpacity
          style={[styles.editBtn, { borderColor: colors.cardBorder, backgroundColor: 'rgba(51,65,85,0.05)' }]}
          onPress={onEdit}
          activeOpacity={0.7}
        >
          <Ionicons name="create-outline" size={15} color={colors.textPrimary} />
        </TouchableOpacity>
      )}

      {/* Content */}
      <View style={styles.content}>
        <Text style={[styles.title, { color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
          {title}
        </Text>
        {description ? (
          <Text style={[styles.description, { color: '#64748B', textAlign: isRTL ? 'right' : 'left' }]}>
            {description}
          </Text>
        ) : null}

        {/* Date + Weight badges row */}
        {(dateRange || weight > 0) ? (
          <View style={[styles.badgesRow, { justifyContent: isRTL ? 'flex-end' : 'flex-start' }]}>
            {weight > 0 && (
              <View style={styles.badge}>
                <Text style={[styles.badgeText, { color: colors.textPrimary }]}>
                  {weight}%
                </Text>
                <Ionicons name="scale-outline" size={20} color={colors.textPrimary} />
              </View>
            )}
            {dateRange ? (
              <View style={styles.badge}>
                <Text style={[styles.badgeText, { color: colors.textPrimary }]}>
                  {isRTL && dateRange.includes(' - ') ? dateRange.split(' - ').reverse().join(' - ') : dateRange}
                </Text>
                <Ionicons name="calendar-outline" size={20} color={colors.textPrimary} />
              </View>
            ) : null}
          </View>
        ) : null}

        {/* Entity tag */}
        {entityName ? (
          <View style={[styles.tagRow, { justifyContent: isRTL ? 'flex-end' : 'flex-start' }]}>
            <View style={styles.entityTag}>
              <Text style={styles.entityTagText}>{entityName}</Text>
            </View>
          </View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    justifyContent: 'flex-end',
  },
  editBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    gap: 8,
    alignItems: 'flex-end',
  },
  title: {
    fontSize: 20,
    fontFamily: FONTS.bold,
    lineHeight: 26,
  },
  description: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    lineHeight: 24,
  },
  badgesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  badgeText: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    lineHeight: 24,
  },
  tagRow: {
    flexDirection: 'row',
    marginTop: 4,
  },
  entityTag: {
    backgroundColor: '#A6813F',
    borderRadius: 40,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  entityTagText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#FFFFFF',
  },
});
